"use client"
import Image from "next/image"
import { useTranslations } from "next-intl"
import Flag from "react-world-flags"
import dynamic from "next/dynamic"
const DotLottieReact = dynamic(() => import("@lottiefiles/dotlottie-react").then((mod) => mod.DotLottieReact), { ssr: false })
import { ChevronRight, ExternalLink } from "lucide-react"
import { PiStarFourFill } from "react-icons/pi"
import { FaDiscord, FaTiktok, FaInstagram, FaYoutube, FaCrown } from "react-icons/fa6"
import { HiCheckBadge } from "react-icons/hi2"
import { SiAmd } from "react-icons/si"
import { BsAmd } from "react-icons/bs"

const floatingLogos = [
  { size: 48, style: { top: "8%", left: "10%", animationDelay: "0s", animationDuration: "4.2s" } },
  { size: 36, style: { top: "20%", right: "6%", animationDelay: "1.1s", animationDuration: "3.8s" } },
  { size: 56, style: { top: "55%", left: "4%", animationDelay: "0.6s", animationDuration: "5.0s" } },
  { size: 32, style: { top: "72%", right: "12%", animationDelay: "1.8s", animationDuration: "4.5s" } },
  { size: 44, style: { top: "38%", right: "2%", animationDelay: "2.4s", animationDuration: "3.6s" } },
]


import { useState, useEffect } from "react"

const Hero = () => {
  const [isDesktop, setIsDesktop] = useState(true);
  const t = useTranslations("Hero");

  useEffect(() => {
    const checkIsDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);
    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  return (
    <div className="relative z-10  flex items-center justify-center overflow-hidden font-poppins">

      <div className="pt-4 relative w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 flex flex-col lg:flex-row items-center justify-between gap-12">
        <style>{`
          @keyframes scroll-arrow {
            0%   { opacity: 0; transform: translateY(-4px); }
            40%  { opacity: 0.75; transform: translateY(0px); }
            80%  { opacity: 0; transform: translateY(4px); }
            100% { opacity: 0; transform: translateY(4px); }
          }
          .arrow-ani {
            animation: scroll-arrow 1.6s ease-in-out infinite;
          }
          .arrow-ani-1 {
            animation-delay: 0.5s;
          }
        `}</style>
        <div className="flex flex-col items-start text-left flex-1 pt-14">
          <div className="relative bg-[#6D28D9] rounded-tr-xl rounded-bl-xl mb-2 inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white font-poppins select-none">
            <FaCrown className="absolute -top-3 -left-2.5 w-5 h-5 text-orange-300 drop-shadow-sm" style={{ transform: "rotate(-27deg)" }} />
            {t("badge_prefix")} <b>{t("badge_country")}</b>
            <Flag code="LK" alt="Sri Lanka Flag" style={{ width: 26, height: 18, display: "inline-block", verticalAlign: "middle", imageRendering: "pixelated" }} />
          </div>
          <div className="relative inline-block mb-4">
            <h1
              className="text-3xl sm:text-3xl md:text-5xl upperscale font-bold font-akira text-white leading-tight"
            >
              {t("title_line1")} <br />{t("title_line2")} <br />{t("title_line3")}
            </h1>
          </div>
          <ul className="mb-10 flex flex-col gap-2 font-poppins">
            {[
              t("feature_1"),
              t("feature_2"),
              t("feature_3"),
              t("feature_4"),

            ].map((item) => (
              <li key={item} className="flex items-center gap-2 text-base sm:text-lg text-gray-400">
                <span className="text-[#8B5CF6] text-lg leading-none"><HiCheckBadge /></span>
                {item}
              </li>
            ))}
            <li className="flex items-center gap-2 text-base sm:text-lg text-gray-400">
              <span className="text-[#8B5CF6] text-lg leading-none"><HiCheckBadge /></span>
              {t("powered_by")}{" "}
              <span className="flex items-center gap-1.5 text-white border-b-2 border-dashed border-white/50 pb-px">
                <BsAmd className="w-4 h-4" />
                {t("hardware")}
              </span>
            </li>
          </ul>
          <div className="pb-16 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-4 mt-2 sm:mt-0">
            <a 
              href="#services" 
              onClick={(e) => {
                e.preventDefault();
                const lenis = (window as any).lenis;
                if (lenis) lenis.scrollTo('#services');
                else window.location.hash = 'services';
              }}
              className="bg-gradient-to-r from-transparent via-[#8B5CF6]/60 to-[#8B5CF6] text-white px-10 py-4 rounded-r-full font-semibold text-base hover:to-[#7C3AED] transition-colors font-poppins flex items-center gap-2 group"
            >
              {t("get_started")}
              <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a 
              href="#locations" 
              onClick={(e) => {
                e.preventDefault();
                const lenis = (window as any).lenis;
                if (lenis) lenis.scrollTo('#locations');
                else window.location.hash = 'locations';
              }}
              className="flex items-center gap-1.5 text-gray-300 hover:text-white text-sm font-poppins transition-colors underline underline-offset-4 decoration-gray-600 hover:decoration-white"
            >
              {t("test_connection")}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <svg aria-hidden="true" width="30" height="44" viewBox="0 0 30 44" fill="none" xmlns="http://www.w3.org/2000/svg" className="mt-8">
            <rect width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani" />
            <rect x="6" y="6" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani" />
            <rect x="12" y="12" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani" />
            <rect x="18" y="6" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani" />
            <rect x="24" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani" />
            <rect y="26" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani arrow-ani-1" />
            <rect x="6" y="32" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani arrow-ani-1" />
            <rect x="12" y="38" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani arrow-ani-1" />
            <rect x="18" y="32" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani arrow-ani-1" />
            <rect x="24" y="26" width="6" height="6" fill="#D9D9D9" fillOpacity="0.75" className="arrow-ani arrow-ani-1" />
          </svg>
        </div>
        <div className="hidden lg:flex flex-1 items-start justify-end relative self-start -mt-24 pt-16 translate-x-10">
          <div style={{ flexShrink: 0 }}>
            {isDesktop && (
              <div aria-hidden="true">
                <DotLottieReact
                  src="https://cdn.aurex.lk/Web-Server.lottie"
                  loop
                  autoplay
                  style={{ height: "700px", width: "700px" }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Hero
